import * as React from 'react'

import config from '@app/config'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import GoogleMap, { fitBounds, NESWBounds } from 'google-map-react'
import Cluster from '@components/cluster/cluster'
import useSupercluster from 'use-supercluster'

import Loader from 'react-loader-spinner'
import MarkerChantier from '@components/marker/business'
import MarkerDeclaration from '@components/marker/declaration'

interface Props {
  navigation: Navigation
  screenStatus: ScreenStatus
  business?: BusinessDetails
  setDeclarationFocused: (declarationId: string) => void
  declarationFocused?: string
}

const PicturesDetailsMap = ({
  navigation,
  screenStatus,
  business,
  setDeclarationFocused,
  declarationFocused,
}: Props) => {
  const mapRef = React.useRef()
  const [mapZoom, setMapZoom] = React.useState<number>(6)
  const [mapBounds, setMapBounds] = React.useState<number[] | null>(null)

  const businessPoint: MapPoint | undefined = React.useMemo(
    () =>
      business && business.longitude && business.latitude
        ? {
            type: 'Feature',
            properties: {
              cluster: false,
              id: business.id,
              state: business.state,
              title: business.title,
              source: business.source,
            },
            geometry: {
              type: 'Point',
              coordinates: [business.longitude, business.latitude],
            },
          }
        : undefined,
    [business]
  )

  const picturesPoints: MapPoint[] = React.useMemo(
    () =>
      business
        ? business.declarations.reduce((acc, cur) => {
            acc.push({
              type: 'Feature',
              properties: {
                cluster: false,
                id: cur.id,
                state: 'picture',
                photoCount: cur.pictures.length,
              },
              geometry: {
                type: 'Point',
                coordinates: [cur.location.longitude, cur.location.latitude],
              },
            })
            return acc
          }, [] as MapPoint[])
        : [],
    [business]
  )

  const points: MapPoint[] = React.useMemo(
    () => (businessPoint ? [businessPoint, ...picturesPoints] : picturesPoints),
    [businessPoint, picturesPoints]
  )

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: mapBounds,
    zoom: mapZoom,
    options: { radius: 75, maxZoom: 22 },
  })

  React.useEffect(() => {
    if (points.length !== 0) {
      const newBounds = points.reduce(
        (acc, cur) => {
          return {
            ne: {
              lat: Math.max(cur.geometry.coordinates[1], acc.ne.lat),
              lng: Math.max(cur.geometry.coordinates[0], acc.ne.lng),
            },
            sw: {
              lat: Math.min(cur.geometry.coordinates[1], acc.sw.lat),
              lng: Math.min(cur.geometry.coordinates[0], acc.sw.lng),
            },
          }
        },
        {
          ne: {
            lat: -10000,
            lng: -10000,
          },
          sw: {
            lat: 100000,
            lng: 100000,
          },
        } as NESWBounds
      )
      const map: any = mapRef.current
      const mapDiv = document.getElementById('map-div')
      const mapSize = {
        width: mapDiv ? mapDiv.clientWidth : 640,
        height: mapDiv ? mapDiv.clientHeight : 380,
      }
      const { center, zoom } = fitBounds(newBounds, mapSize)
      if (newBounds.ne.lat === newBounds.sw.lat && newBounds.ne.lng === newBounds.sw.lng) {
        map && map.setZoom(22)
        map && map.panTo(newBounds.ne)
      } else {
        map && map.setZoom(zoom - 1)
        map && map.panTo(center)
      }
    }
  }, [points])

  const renderMarker = (cluster: any, expandedRadius?: number, expandedNumber?: number) => {
    const [longitude, latitude] = cluster.geometry.coordinates
    const { cluster: isCluster, point_count: pointCount } = cluster.properties
    if (isCluster) {
      return (
        <Cluster
          key={cluster.id}
          lat={latitude}
          lng={longitude}
          size={40}
          count={pointCount}
          onClick={() => {
            const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(cluster.id), 22)
            const map: any = mapRef.current
            map && map.setZoom(expansionZoom)
            map && map.panTo({ lat: latitude, lng: longitude })
          }}
        />
      )
    }

    if (cluster.properties.state === 'picture') {
      return (
        <MarkerDeclaration
          key={cluster.id}
          size={60}
          lat={latitude}
          lng={longitude}
          focused={declarationFocused === cluster.properties.id}
          onClick={() =>
            setDeclarationFocused(declarationFocused === cluster.properties.id ? undefined : cluster.properties.id)
          }
          photoCount={cluster.properties.photoCount}
          expandedRadius={expandedRadius}
          expandedNumber={expandedNumber}
        />
      )
    }

    return (
      <MarkerChantier
        size={40}
        key={cluster.id}
        lat={latitude}
        lng={longitude}
        type={cluster.properties.state}
        source={cluster.properties.source}
        title={cluster.properties.title}
        focused={false}
        hideTitle={true}
        expandedRadius={expandedRadius}
        expandedNumber={expandedNumber}
      />
    )
  }

  return (
    <Container id="map-div">
      <GoogleMap
        bootstrapURLKeys={{ key: config.GMAP_APIKEY }}
        defaultCenter={{
          lat: 47.09,
          lng: 2.25,
        }}
        defaultZoom={6}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
        }}
        onChange={({ zoom, bounds }) => {
          setMapZoom(zoom)
          setMapBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat])
        }}>
        {screenStatus === 'loading' ? (
          <StatusContainer>
            <Loader type="ThreeDots" color={Theme.colors.mapButtonLoader} height={30} />
          </StatusContainer>
        ) : (
          clusters.map((cluster) => {
            const { cluster: isCluster } = cluster.properties
            if (mapZoom < 22 || !isCluster) {
              return renderMarker(cluster)
            } else {
              const subClusters = supercluster.getChildren(cluster.id)
              return subClusters.map((sub: any, idx: number) => renderMarker(sub, idx + 1, subClusters.length))
            }
          })
        )}
      </GoogleMap>
    </Container>
  )
}

export default PicturesDetailsMap

const Container = styled('div')`
  display: flex;
  flex: 1;
`
const StatusContainer = styled('div')`
  position: absolute;
`
